import { Controller } from '@hotwired/stimulus';
import { constructURL } from 'helpers/url';
import useResetForm from 'stimulus-hooks/use-reset-form';

enum ModeType {
  InPerson = '50658e86-5837-4816-a2c1-9870ebbe987b',
  Teletherapy = 'b2c74159-e950-426a-8157-d7d568dba95e',
  OutOfOffice = 'dde9d33c-8fb5-4f49-a8f9-1f4b19e085f4',
  Other = 'c142e891-9ad2-4421-8dab-d28e571b6098',
}

type ModeTypeIds = ModeType[keyof ModeType];

export default class extends Controller {
  static targets = [
    'radio',
    'text',
    'lazyLoad',
    'observer',
    'patients',
    'dropdownBtn',
    'modeRadio',
    'allDayModeCheckbox',
    'patientNameDefaultTitle',
    'eventTypeDefaultTitle',
  ];

  static values = {
    randomId: String,
    formType: {
      default: 'events-new-form',
      type: String,
    },
  };

  initialSrc = '';
  selectedMode?: HTMLInputElement;

  connect() {
    useResetForm(this, { element: this.form });
    this.initialSrc = this.lazyLoadSrc;
  }

  modeRadioTargetConnected(target: HTMLInputElement) {
    if (target.checked) {
      this.selectedMode = target;
    }
  }

  selectType() {
    const radio = this.radioTargets.find((r) => r.checked);
    const displayValue = radio?.dataset.displayValue;
    if (!displayValue) return;

    this.textTarget.innerText = displayValue;
  }

  refetchTypes(event: Event) {
    if (!this.lazyLoadSrc) return;
    const { target } = event;
    if (!(target instanceof HTMLInputElement)) return;

    const url = constructURL(this.lazyLoadSrc, { event_mode_id: target.value, event_type_id: null });
    this.lazyLoadSrc = url.toString();
  }

  resetForm() {
    const selectedMode = this.selectedMode?.value as ModeTypeIds | undefined;
    if (selectedMode) {
      this.selectModeType(selectedMode);
    }

    if (this.initialSrc) {
      const url = constructURL(this.initialSrc, { refetch_time: new Date().getTime().toString() });
      this.lazyLoadSrc = url.toString();
    }
  }

  enableDropdown() {
    if (!this.hasDropdownBtnTarget) return;
    this.dropdownBtnTarget.disabled = false;
  }

  disableDropdown() {
    if (!this.hasDropdownBtnTarget) return;
    this.dropdownBtnTarget.disabled = true;
  }

  hideObserverAddition() {
    if (!this.hasObserverTarget) return;
    this.observerTarget.setAttribute('hidden', '');
  }

  hidePatientsAddition() {
    if (this.hasPatientsTarget) {
      this.patientsTarget.hidden = true;
    }

    const autocomplete = this.patientsTarget.querySelector('awc-autocomplete');
    if (!(autocomplete instanceof HTMLElement)) return;
    autocomplete.required = false;
  }

  showObserverAddition() {
    if (!this.hasObserverTarget) return;
    this.observerTarget.hidden = false;
  }

  showPatientsAddition() {
    if (this.hasPatientsTarget) {
      this.patientsTarget.hidden = false;
    }

    const autocomplete = this.patientsTarget.querySelector('awc-autocomplete');
    if (!(autocomplete instanceof HTMLElement)) return;

    autocomplete.required = true;
  }

  updateFields(event: Event) {
    const { target } = event;
    if (!(target instanceof HTMLInputElement)) return;
    this.selectModeType(target.value as ModeTypeIds);
  }

  private selectModeType(value: ModeTypeIds) {
    if (value === ModeType.OutOfOffice || value === ModeType.Other) {
      this.hideObserverAddition();
      this.hidePatientsAddition();
    } else if (value === ModeType.InPerson) {
      this.hideObserverAddition();
      this.showPatientsAddition();
    } else if (value === ModeType.Teletherapy) {
      this.showObserverAddition();
      this.showPatientsAddition();
    }

    if (value === ModeType.OutOfOffice || value === ModeType.Other) {
      this.eventTypeDefaultTitleTarget.hidden = false;
      this.patientNameDefaultTitleTarget.hidden = true;
    } else {
      this.eventTypeDefaultTitleTarget.hidden = true;
      this.patientNameDefaultTitleTarget.hidden = false;
    }

    if (this.formTypeValue === 'events-new-form') {
      if (value === ModeType.OutOfOffice && !this.allDayModeCheckboxTarget.checked) {
        this.allDayModeCheckboxTarget.click();
      } else if (this.allDayModeCheckboxTarget.checked && value !== ModeType.OutOfOffice) {
        this.allDayModeCheckboxTarget.click();
      }
    }
  }

  private get lazyLoadSrc() {
    return this.lazyLoadTarget.getAttribute('data-lazy-load-src-value') || '';
  }

  private set lazyLoadSrc(src: string) {
    this.lazyLoadTarget.setAttribute('data-lazy-load-src-value', src);
  }

  private get form() {
    return this.element.closest('form') as HTMLFormElement;
  }

  declare readonly radioTargets: HTMLInputElement[];
  declare readonly textTarget: HTMLElement;
  declare readonly lazyLoadTarget: HTMLElement;
  declare readonly dropdownBtnTarget: HTMLButtonElement;
  declare readonly observerTarget: HTMLElement;
  declare readonly modeRadioTargets: HTMLInputElement[];
  declare readonly allDayModeCheckboxTarget: HTMLInputElement;
  declare readonly eventTypeDefaultTitleTarget: HTMLElement;
  declare readonly patientNameDefaultTitleTarget: HTMLElement;
  declare readonly hasDropdownBtnTarget: boolean;
  declare readonly hasObserverTarget: boolean;
  declare readonly hasPatientsTarget: boolean;
  declare readonly randomIdValue: string;
  declare readonly formTypeValue: string;
  declare readonly patientsTarget: HTMLElement;
}
